

import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMunicipalities } from "../redux/actions/municipalitiesActions";
import theme from "../components/shared/theme";
import { thStyle, tdStyle } from "../components/shared/themeStyles";
import EditMunicipalityModal from "../pages/Municipality/EditMunicipalityModal";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const ITEMS_PER_PAGE = 10;

const ViewMunicipalities = () => {
  const dispatch = useDispatch();
  const { municipalities = [], loading, error } = useSelector(
    (state) => state.municipalitiesData || {}
  );

  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchMunicipalities());
  }, [dispatch]);

  const handleEditClick = (municipality) => {
    setSelectedMunicipality(municipality);
  };

  const handleCloseModal = () => {
    setSelectedMunicipality(null);
    dispatch(fetchMunicipalities());
  };

  const handleExportCSV = () => {
    const headers = ["ID", "Municipality Name"];
    const csvRows = [
      headers.join(","),
      ...municipalities.map((m) => `${m.id},"${m.name}"`),
    ];

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "municipalities.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil(municipalities.length / ITEMS_PER_PAGE);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return municipalities.slice(start, start + ITEMS_PER_PAGE);
  }, [municipalities, currentPage]);

  const paginationButtonStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
    padding: "0.4rem 0.8rem",
    border: "1px solid #ccc",
    borderRadius: "0.375rem",
    backgroundColor: "#f9f9f9",
    cursor: "pointer",
    fontSize: theme.font.size.sm,
    color: theme.colors.grayDark,
    transition: "background 0.2s",
    disabled: {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  };

  return (
    <div
      style={{
        backgroundColor: theme.colors.background,
        padding: theme.spacing.md,
        minHeight: "100vh",
      }}
    >
      <div className="flex justify-between items-center mb-4">
        <h1
          style={{
            fontSize: theme.font.size["2xl"],
            fontWeight: theme.font.weight.bold,
          }}
        >
          Municipalities List
        </h1>
        <button
          onClick={handleExportCSV}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm"
        >
          Export CSV
        </button>
      </div>

      {loading ? (
        <p style={{ fontSize: theme.font.size.base }}>Loading...</p>
      ) : error ? (
        <div
          style={{
            backgroundColor: theme.colors.dangerLight,
            border: `1px solid ${theme.colors.danger}`,
            color: theme.colors.dangerText,
            padding: theme.spacing.sm,
            borderRadius: theme.radius.md,
            marginBottom: theme.spacing.md,
          }}
        >
          Error: {error}
        </div>
      ) : municipalities.length > 0 ? (
        <>
          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="min-w-[400px] w-full text-sm text-left">
              <thead
                style={{
                  backgroundColor: theme.colors.grayDark,
                  color: "white",
                }}
              >
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Municipality Name</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedData.map((m, index) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td style={tdStyle}>
                      {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                    </td>
                    <td style={tdStyle}>{m.name}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={() => handleEditClick(m)}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Stylish Pagination Controls */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: theme.spacing.md,
            }}
          >
            <button
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                ...paginationButtonStyle,
                ...(currentPage === 1 ? paginationButtonStyle.disabled : {}),
              }}
            >
              <FiChevronLeft /> Previous
            </button>
            <span style={{ fontSize: theme.font.size.sm }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                currentPage < totalPages && setCurrentPage(currentPage + 1)
              }
              disabled={currentPage === totalPages}
              style={{
                ...paginationButtonStyle,
                ...(currentPage === totalPages
                  ? paginationButtonStyle.disabled
                  : {}),
              }}
            >
              Next <FiChevronRight />
            </button>
          </div>
        </>
      ) : (
        <p style={{ fontSize: theme.font.size.base }}>
          No municipalities found.
        </p>
      )}

      {/* Edit Modal */}
      {selectedMunicipality && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-lg relative">
            <button
              onClick={handleCloseModal}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-xl font-bold"
            >
              {/* × */}
            </button>
            <EditMunicipalityModal
              selectedMunicipality={selectedMunicipality}
              onClose={handleCloseModal}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewMunicipalities;
